import { Check, Loader2, Pause, Play, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import {
  fetchVoiceSample,
  listVoices,
  selectVoice,
  type LiveVoice,
  type VoiceCatalog,
} from '../../lib/voiceAssistantApi';

interface Props {
  /** The voice currently in effect (from /status); the panel highlights it. */
  onClose: () => void;
  /** Called after a pick is saved so the page can refresh its header. */
  onSelected: (voice: string) => void;
  /** A live session keeps the current voice; the pick applies to the next one. */
  sessionLive: boolean;
}

type SampleState = 'idle' | 'loading' | 'playing' | 'error';

const GROUP_ORDER: LiveVoice['group'][] = ['recommended', 'live', 'classic'];

/**
 * Voice picker for Firmio Voice: every GPT-Live voice with a play button that
 * auditions a personalised greeting ("Salutare, Andrei! …") spoken by that
 * exact voice, and a "use this one" action that saves the per-user choice.
 */
export function VoiceSettingsPanel({ onClose, onSelected, sessionLive }: Props) {
  const { t } = useI18n();
  const [catalog, setCatalog] = useState<VoiceCatalog | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [sample, setSample] = useState<{
    voice: string;
    state: SampleState;
    error?: string;
  } | null>(null);
  // Playback goes through ONE AudioContext that is created/resumed inside the
  // tap handler: iOS Safari refuses `audio.play()` once an await (the fetch)
  // separates it from the user gesture, but a context unlocked in the gesture
  // stays unlocked, so a buffer started after the download still plays.
  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const bufferCache = useRef(new Map<string, AudioBuffer>());

  useEffect(() => {
    void listVoices()
      .then(setCatalog)
      .catch((err: unknown) => setLoadError(err instanceof Error ? err.message : String(err)));
    return () => {
      try {
        sourceRef.current?.stop();
      } catch {
        // already stopped
      }
      sourceRef.current = null;
      void ctxRef.current?.close().catch(() => undefined);
      ctxRef.current = null;
      bufferCache.current.clear();
    };
  }, []);

  const stopSample = useCallback(() => {
    try {
      sourceRef.current?.stop();
    } catch {
      // already stopped
    }
    sourceRef.current = null;
    setSample(null);
  }, []);

  const playSample = useCallback(
    async (voice: string) => {
      if (sample?.voice === voice && sample.state === 'playing') {
        stopSample();
        return;
      }
      stopSample();
      // Unlock synchronously, inside the gesture.
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) {
        setSample({ voice, state: 'error', error: t('voice.settings.sampleError') });
        return;
      }
      if (!ctxRef.current) ctxRef.current = new Ctor();
      const ctx = ctxRef.current;
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
      setSample({ voice, state: 'loading' });
      try {
        let buffer = bufferCache.current.get(voice);
        if (!buffer) {
          const bytes = await fetchVoiceSample(voice);
          buffer = await ctx.decodeAudioData(bytes);
          bufferCache.current.set(voice, buffer);
        }
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => {
          if (sourceRef.current === source) sourceRef.current = null;
          setSample((s) => (s?.voice === voice ? null : s));
        };
        sourceRef.current = source;
        source.start();
        setSample({ voice, state: 'playing' });
      } catch (err) {
        setSample({
          voice,
          state: 'error',
          error: err instanceof Error ? err.message : t('voice.settings.sampleError'),
        });
      }
    },
    [sample, stopSample, t],
  );

  const choose = useCallback(
    async (voice: string) => {
      if (!catalog || catalog.selected === voice) return;
      setSaving(voice);
      try {
        const selected = await selectVoice(voice);
        setCatalog((c) => (c ? { ...c, selected } : c));
        onSelected(selected);
      } catch {
        // The header keeps the old voice; the user can retry.
      } finally {
        setSaving(null);
      }
    },
    [catalog, onSelected],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden rounded-lg border border-border bg-background">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-sm font-semibold text-foreground">
            {t('voice.settings.title')}
          </h2>
          <p className="text-xs text-muted-foreground">
            {sessionLive ? t('voice.settings.appliesNext') : t('voice.settings.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label={t('common.close')}
        >
          <X size={16} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {loadError && (
          <p className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
            {loadError}
          </p>
        )}
        {!catalog && !loadError && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" />
            {t('common.loading')}
          </p>
        )}
        {catalog && (
          <>
            <p className="mb-4 break-words rounded-md bg-accent/50 px-3 py-2 text-xs text-muted-foreground">
              {t('voice.settings.greetingHint')} „{catalog.greeting}”
            </p>
            {GROUP_ORDER.map((group) => {
              const voices = catalog.voices.filter((v) => v.group === group);
              if (!voices.length) return null;
              return (
                <section key={group} className="mb-5">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(`voice.settings.group.${group}`)}
                  </h3>
                  <ul className="grid gap-2 lg:grid-cols-2">
                    {voices.map((v) => {
                      const isSelected = catalog.selected === v.id;
                      const thisSample = sample?.voice === v.id ? sample : null;
                      return (
                        <li
                          key={v.id}
                          className={`flex min-w-0 items-center gap-3 rounded-lg border p-3 ${
                            isSelected ? 'border-primary bg-primary/5' : 'border-border bg-card'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => void playSample(v.id)}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground hover:bg-accent"
                            title={t('voice.settings.play')}
                            aria-label={`${t('voice.settings.play')} ${v.label}`}
                          >
                            {thisSample?.state === 'loading' ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : thisSample?.state === 'playing' ? (
                              <Pause size={16} />
                            ) : (
                              <Play size={16} />
                            )}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-foreground">{v.label}</span>
                              {v.id === catalog.default && (
                                <span className="rounded bg-accent px-1.5 py-0.5 text-[10px] uppercase text-accent-foreground">
                                  {t('voice.settings.default')}
                                </span>
                              )}
                            </div>
                            <p
                              className="truncate text-xs text-muted-foreground"
                              title={v.description}
                            >
                              {thisSample?.state === 'error' ? thisSample.error : v.description}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => void choose(v.id)}
                            disabled={isSelected || saving === v.id}
                            className={`flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium ${
                              isSelected
                                ? 'bg-primary text-primary-foreground'
                                : 'border border-border bg-background text-foreground hover:bg-accent'
                            }`}
                          >
                            {saving === v.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : isSelected ? (
                              <Check size={12} />
                            ) : null}
                            {isSelected ? t('voice.settings.selected') : t('voice.settings.use')}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
